/**
 * Utilidades para trabajar con el SDK de Mercado Pago
 */

let mercadoPagoInstance = null;
let sdkPromise = null;
let initPromise = null;

const SDK_URL = 'https://sdk.mercadopago.com/js/v2';

/**
 * Carga el SDK bajo demanda.
 *
 * Antes vivía como <script> síncrono en el <head> de index.html. Eso bloquea el
 * parseo del HTML hasta que el host responde: si la red no deja salir (típico
 * detrás de un portal cautivo, donde el firewall descarta en silencio lo que no
 * está en su lista blanca), la app se queda en blanco hasta el timeout del
 * sistema. Cargarlo aquí lo vuelve opcional: solo lo pagan las pantallas de pago.
 *
 * @returns {Promise<void>} - Se resuelve cuando window.MercadoPago existe
 */
function loadSdk() {
  if (typeof window.MercadoPago !== 'undefined') {
    return Promise.resolve();
  }

  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SDK_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Se descarta la promesa fallida para poder reintentar más tarde.
        sdkPromise = null;
        reject(new Error('No se pudo cargar el SDK de Mercado Pago. Revisa la conexión.'));
      };
      document.head.appendChild(script);
    });
  }

  return sdkPromise;
}

/**
 * Inicializa la instancia de Mercado Pago con la clave pública
 * @returns {Promise<MercadoPago>} - Instancia de Mercado Pago
 */
export function initMercadoPago() {
  if (mercadoPagoInstance) {
    return Promise.resolve(mercadoPagoInstance);
  }

  const publicKey = window.MERCADOPAGO?.PUBLIC_KEY;

  if (!publicKey) {
    return Promise.reject(new Error('Clave pública de Mercado Pago no configurada. Configura window.MERCADOPAGO.PUBLIC_KEY en config.js'));
  }

  // Se cachea la inicialización completa, no solo la carga del script: dos
  // llamadas concurrentes (p. ej. dos formularios montados a la vez) deben
  // compartir una única instancia del SDK.
  if (!initPromise) {
    initPromise = loadSdk()
      .then(() => {
        if (typeof window.MercadoPago === 'undefined') {
          throw new Error('Mercado Pago SDK no está cargado.');
        }

        mercadoPagoInstance = new window.MercadoPago(publicKey, {
          locale: 'es-MX',
        });

        return mercadoPagoInstance;
      })
      .catch((error) => {
        // Se descarta para poder reintentar en la siguiente llamada.
        initPromise = null;
        throw error;
      });
  }

  return initPromise;
}

/**
 * Obtiene la instancia de Mercado Pago (inicializa si es necesario)
 * @returns {Promise<MercadoPago>} - Instancia de Mercado Pago
 */
export function getMercadoPagoInstance() {
  return initMercadoPago();
}

/**
 * Convierte payment_type_id de MercadoPago a cardType del backend.
 * @param {string} paymentTypeId - 'credit_card' | 'debit_card'
 * @returns {'C'|'D'}
 */
export function paymentTypeIdToCardType(paymentTypeId) {
  return paymentTypeId === 'debit_card' ? 'D' : 'C';
}
